import { useCallback, useState } from "react";
import { useSaveStaffOrder } from "../../../hooks/useSaveStaffOrder";
import { useCheckAPI } from "../../../hooks/useCheckAPI";
import { useQueryClient } from "@tanstack/react-query";
import {
  applyBasketChoice,
  findBasketConflicts,
  resolveBackendLineNo,
} from "../../../utils/basketLines";

export function useOrderItemActions({
  item,
  itemLineKey,
  backendLineNo,
  basketLines = [],
  order,
  person,
  staffRequest,
  availalbeEntitlement,
  fieldPrefix,
  resetField,
  setAlertNotification,
  orderType = "draft",
  overEntitlementAction = "No Action",
  userType = "",
}) {
  const queryClient = useQueryClient();
  const [entitlementDialogOpen, setEntitlementDialogOpen] = useState(false);
  const [draftEntitlementDialogOpen, setDraftEntitlementDialogOpen] =
    useState(false);
  const [draftEntitlementMode, setDraftEntitlementMode] = useState("Warning");
  const [overEntitlementReason, setOverEntitlmentReason] = useState("");
  const [dataToSave, setDataToSave] = useState({});
  const [basketConflicts, setBasketConflicts] = useState([]);

  const {
    mutate: saveOrder,
    isSuccess: saveSuccess,
    data: saveData,
  } = useSaveStaffOrder();

  const handleAddItem = useCallback(
    (data) => {
      const itemData = data.items[itemLineKey];
      const isOverEntitlement =
        Number(itemData.quantity) > availalbeEntitlement;
      const lineNo = resolveBackendLineNo(
        basketLines,
        {
          productCode: item.productCode,
          colour: itemData.colour,
          size: itemData.size,
          fit: itemData.fit,
        },
        backendLineNo,
      );
      const payload = {
        data: {
          filter: { orderNo: order.no, staffLineNo: person.staffLineNo },
          lines: [
            {
              lineNo,
              productCode: item.productCode,
              colour: itemData.colour,
              size: itemData.size,
              fit: itemData.fit,
              quantity: itemData.quantity,
              overEntitlementReason: null,
            },
          ],
          reason: person.reasonCode,
          staffpoNo: person.staffpoNo,
        },
      };
      setDataToSave(payload);

      // Staff Request entitlement check (existing logic)
      if (staffRequest && overEntitlementReason === "" && isOverEntitlement) {
        setEntitlementDialogOpen(true);
        return;
      }

      // Draft Order entitlement check (new logic)
      // Approvers can self-approve, so treat "Escalate" as "Warning" for them
      const effectiveAction =
        userType === "Approver" && overEntitlementAction === "Escalate"
          ? "Warning"
          : overEntitlementAction;

      if (
        !staffRequest &&
        orderType === "draft" &&
        isOverEntitlement &&
        effectiveAction !== "No Action"
      ) {
        if (effectiveAction === "Block") {
          setDraftEntitlementMode("Block");
          setDraftEntitlementDialogOpen(true);
          return; // Block - do not save
        }
        if (effectiveAction === "Warning" || effectiveAction === "Escalate") {
          if (overEntitlementReason === "") {
            setDraftEntitlementMode(effectiveAction);
            setDraftEntitlementDialogOpen(true);
            return; // Wait for reason
          }
        }
      }

      // Bulk lists can repeat a variant, so ask whether to add or overwrite
      if (orderType === "bulk") {
        const conflicts = findBasketConflicts(basketLines, payload.data.lines);
        if (conflicts.length > 0) {
          setBasketConflicts(conflicts);
          return;
        }
      }

      saveOrder(payload);
    },
    [
      item.productCode,
      itemLineKey,
      backendLineNo,
      basketLines,
      order.no,
      person.staffLineNo,
      person.reasonCode,
      person.staffpoNo,
      staffRequest,
      overEntitlementReason,
      availalbeEntitlement,
      saveOrder,
      orderType,
      overEntitlementAction,
    ],
  );

  // Handler for DraftEntitlementDialog confirmation
  const handleDraftEntitlementConfirm = useCallback(
    (reason) => {
      const updatedPayload = {
        ...dataToSave,
        data: {
          ...dataToSave.data,
          lines: [
            {
              ...dataToSave.data.lines[0],
              overEntitlementReason: reason,
            },
          ],
        },
      };
      setDataToSave(updatedPayload);
      saveOrder(updatedPayload);
    },
    [dataToSave, saveOrder],
  );

  const handleBasketChoice = useCallback(
    (choice) => {
      const updatedPayload = {
        ...dataToSave,
        data: {
          ...dataToSave.data,
          lines: applyBasketChoice(
            dataToSave.data.lines,
            basketConflicts,
            choice,
          ),
        },
      };
      setBasketConflicts([]);
      setDataToSave(updatedPayload);
      saveOrder(updatedPayload);
    },
    [dataToSave, basketConflicts, saveOrder],
  );

  const cancelBasketChoice = useCallback(() => setBasketConflicts([]), []);

  const saveSuccessFunc = () => {
    resetField(`${fieldPrefix}.colour`, { defaultValue: "" });
    resetField(`${fieldPrefix}.size`, { defaultValue: "" });
    resetField(`${fieldPrefix}.fit`, { defaultValue: "" });
    resetField(`${fieldPrefix}.quantity`, { defaultValue: 0 });
    setAlertNotification({
      message: "Item added to order",
      severity: "success",
    });
    queryClient.invalidateQueries({
      queryKey: ["draftOrderStaff", order.no, person.staffLineNo],
    });
    queryClient.invalidateQueries({
      queryKey: ["wardrobeCard", person.uniformId, person.no],
    });
  };

  const saveErrorFunc = () => {
    setAlertNotification({
      message: `Error: ${saveData?.data?.status}: ${saveData?.data?.statusText}`,
      severity: "error",
    });
  };

  useCheckAPI(
    saveSuccess,
    saveData,
    saveData?.data?.status,
    saveSuccessFunc,
    saveErrorFunc,
  );

  return {
    entitlementDialogOpen,
    setEntitlementDialogOpen,
    draftEntitlementDialogOpen,
    setDraftEntitlementDialogOpen,
    draftEntitlementMode,
    overEntitlementReason,
    setOverEntitlmentReason,
    dataToSave,
    setDataToSave,
    handleAddItem,
    handleDraftEntitlementConfirm,
    basketConflicts,
    handleBasketChoice,
    cancelBasketChoice,
    saveOrder,
  };
}
